import { AlertTriangle, FileText } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import type {
  ImpactAnalysisCalculatedResult,
  ImpactLevel,
} from "@/types/impact-analysis";
import BooleanSelect, {
  getSelectTriggerClassName,
} from "@/components/onboarding/shared/BooleanSelect";
import { FieldTooltip } from "@/components/onboarding/shared/FieldTooltip";
import { ARTICLE9_CONFIG } from "@/utils/article9Config";

type LevelSelectProps = {
  value: ImpactLevel | null;
  onChange: (value: ImpactLevel | null) => void;
};

function LevelSelect({ value, onChange }: LevelSelectProps) {
  return (
    <Select
      value={value ?? ""}
      onValueChange={(val) => onChange((val || null) as ImpactLevel | null)}
    >
      <SelectTrigger
        className={getSelectTriggerClassName(
          value === "RIDICAT"
            ? "Ridicat"
            : value === "MEDIU"
              ? "Mediu"
              : value === "SCĂZUT"
                ? "Scăzut"
                : null
        )}
      >
        <SelectValue placeholder="Selectează..." />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="RIDICAT">Ridicat</SelectItem>
        <SelectItem value="MEDIU">Mediu</SelectItem>
        <SelectItem value="SCĂZUT">Scăzut</SelectItem>
      </SelectContent>
    </Select>
  );
}

type Article9SectionProps = {
  enabled: boolean;
  onToggle: () => void;
  article9Results: ImpactAnalysisCalculatedResult;
  onResultChange: (
    field: keyof ImpactAnalysisCalculatedResult,
    value: unknown
  ) => void;
  required?: boolean;
  fieldErrors?: Record<string, string>;
};

function Article9Section({
  enabled,
  onToggle,
  article9Results,
  onResultChange,
  required = false,
  fieldErrors,
}: Article9SectionProps) {
  if (!enabled && !required) {
    return (
      <div className="text-center py-8 text-ds-text-400">
        <div className="mx-auto w-16 h-16 rounded-full bg-ds-surface-100 flex items-center justify-center mb-3">
          <FileText className="w-8 h-8 text-ds-border-300" />
        </div>
        <p className="text-sm font-medium">Secțiune opțională</p>
        <p className="text-xs mt-1 mb-4">
          Activează pentru a completa evaluarea conform Art. 9
        </p>
        <button
          type="button"
          onClick={onToggle}
          className="text-sm font-medium text-ds-blue-600 hover:text-ds-blue-700 underline underline-offset-2"
        >
          Activează secțiunea
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <Table className="border-2 border-black">
          <TableBody>
            <ArticleRow
              label={
                <>
                  Art. 9 <br /> lit. a)
                </>
              }
              config={ARTICLE9_CONFIG.article9a}
              selectComponent={
                <BooleanSelect
                  value={article9Results.article9a}
                  onChange={(val) => onResultChange("article9a", val)}
                />
              }
            />
            <ArticleRow
              label={
                <>
                  Art. 9 <br /> lit. b)
                </>
              }
              config={ARTICLE9_CONFIG.article9b}
              selectComponent={
                <LevelSelect
                  value={article9Results.article9b}
                  onChange={(val) => onResultChange("article9b", val)}
                />
              }
            />
            <ArticleRow
              label={
                <>
                  Art. 9 <br /> lit. c)
                </>
              }
              config={ARTICLE9_CONFIG.article9c}
              selectComponent={
                <LevelSelect
                  value={article9Results.article9c}
                  onChange={(val) => onResultChange("article9c", val)}
                />
              }
            />
            <ArticleRow
              label={
                <>
                  Art. 9 <br /> lit. d)
                </>
              }
              config={ARTICLE9_CONFIG.article9d}
              selectComponent={
                <BooleanSelect
                  value={article9Results.article9d}
                  onChange={(val) => onResultChange("article9d", val)}
                />
              }
            />
          </TableBody>
        </Table>
      </div>
      {fieldErrors?.["article9"] && (
        <div className="flex items-center gap-2 p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
          <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
          <span className="text-xs text-destructive">
            {fieldErrors["article9"]}
          </span>
        </div>
      )}
      {!required && (
        <button
          type="button"
          onClick={onToggle}
          className="text-xs text-ds-text-400 hover:text-ds-text-600 underline underline-offset-2"
        >
          Dezactivează secțiunea
        </button>
      )}
    </div>
  );
}

type ArticleRowProps = {
  label: React.ReactNode;
  config: { question: string; tooltip: string };
  selectComponent: React.ReactNode;
};

function ArticleRow({ label, config, selectComponent }: ArticleRowProps) {
  return (
    <TableRow className="border-2 border-black">
      <TableCell className="p-2 font-semibold text-sm border-r-2 border-black whitespace-nowrap align-top bg-ds-blue-400 text-black w-[80px]">
        {label}
      </TableCell>
      <TableCell className="p-2 text-sm border-r-2 border-black align-top bg-ds-blue-200 text-black font-bold">
        <span className="flex items-start gap-1">
          {config.question}
          <FieldTooltip content={config.tooltip} />
        </span>
      </TableCell>
      <TableCell className="p-2 align-middle w-[160px] min-w-[140px]">
        {selectComponent}
      </TableCell>
    </TableRow>
  );
}

export default Article9Section;
